import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from "@mui/material";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"; // Import trash can icon
import { useData } from "../hooks/useData";

const OpenFromBrowserDialog = ({ open, onClose, canvas, onOpenSuccess }) => {
  const { setProjectName, setSnackbar } = useData();
  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Fetch designs from localStorage
  useEffect(() => {
    if (open) {
      const designKeys = Object.keys(localStorage).filter((key) =>
        key.startsWith("design_")
      );
      const designNames = designKeys.map((key) => key.replace("design_", ""));
      setDesigns(designNames);
      setSelectedDesign(null); // Reset selection when dialog opens
    }
  }, [open]);

  const handleSelectDesign = (designName) => {
    setSelectedDesign(designName);
  };

  const handleDeleteDesign = (designName) => {
    localStorage.removeItem(`design_${designName}`);
    setDesigns(designs.filter((name) => name !== designName));
    if (selectedDesign === designName) setSelectedDesign(null);
  };

  const handleOpen = () => {
    if (!selectedDesign || !canvas) return;
    try {
      const designData = localStorage.getItem(`design_${selectedDesign}`);
      if (designData) {
        const parsedData = JSON.parse(designData);
        canvas.clear(); // Clear the canvas before loading new design
        canvas.loadFromJSON(parsedData, () => {
          canvas.setViewportTransform([1, 0, 0, 1, 0, 0]); // Reset viewport
          canvas.setZoom(1); // Reset zoom
          canvas.renderAll(); // Force render
          canvas.requestRenderAll(); // Ensure render is requested
          setProjectName(selectedDesign);
          onOpenSuccess?.();
          onClose();
        });
      }
    } catch (e) {
      console.error("Error loading design from local storage:", e);
      setSnackbar({
        open: true,
        message: "Failed to load design from local storage.",
        severity: "error",
      });
    }
  };

  const handleClose = () => {
    setSelectedDesign(null);
    setDialogPosition({ x: 0, y: 0 });
    onClose();
  };

  // Draggable functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - dialogPosition.x,
      y: e.clientY - dialogPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          minWidth: 400,
          transform: `translate(${dialogPosition.x}px, ${dialogPosition.y}px)`,
        },
      }}
      aria-labelledby="dialog-title"
      maxWidth="sm"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          bgcolor: "primary.main",
          color: "white",
          fontSize: "1.25rem",
          cursor: "move",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          pb: 2,
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIndicatorOutlinedIcon sx={{ fontSize: 20, mr: 1 }} />
        Open from Browser Storage
      </DialogTitle>
      <DialogContent sx={{ mt: 2, pt: 2, pb: 3 }}>
        {designs.length > 0 ? (
          <Stack direction="column" spacing={2}>
            <Typography variant="body1">
              Select a design to open from browser storage:
            </Typography>
            <List
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid #ddd",
                borderRadius: 1,
              }}
              aria-label="Saved designs list"
            >
              {designs.map((designName, index) => (
                <React.Fragment key={designName}>
                  <ListItem
                    button
                    selected={selectedDesign === designName}
                    onClick={() => handleSelectDesign(designName)}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "primary.light",
                        "&:hover": {
                          backgroundColor: "primary.main",
                          color: "white",
                        },
                      },
                      "&:hover": {
                        cursor: "pointer", // Hand cursor on hover
                      },
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label={`Delete ${designName}`}
                        onClick={() => handleDeleteDesign(designName)}
                        color="error"
                      >
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={designName} />
                  </ListItem>
                  {index < designs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            <DialogActions sx={{ justifyContent: "flex-end", px: 0 }}>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={handleOpen}
                disabled={!selectedDesign}
                variant="contained"
                color="primary"
              >
                Open
              </Button>
            </DialogActions>
          </Stack>
        ) : (
          <Stack direction="column" spacing={2} alignItems="center">
            <Typography variant="body1">
              No designs found in browser storage.
            </Typography>
            <Button
              onClick={handleClose}
              variant="contained"
              color="primary"
              sx={{ mt: 1 }}
            >
              Close
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OpenFromBrowserDialog;
